import { Suspense } from "react";
import { Await, useLoaderData } from "react-router-dom";
import { ClimbingBoxLoader } from "react-spinners";
import { VegaEmbed } from "react-vega";
import { Workout } from "../concept2";
import { VEGA_CONFIG, VEGA_HEIGHT, VEGA_WIDTH, VEGA_REGRESSION_COLOR } from "../vegahelpers";

interface ProjectionPoint {
    date: string;
    cumulativeDistance: number;
}

function computeProjectionData(workouts: Workout[]): ProjectionPoint[] {
    if (workouts.length === 0) return [];

    // Sort by date and compute cumulative distances
    const sorted = [...workouts].sort((a, b) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    let cumulative = 0;
    const cumulativeData = sorted.map(w => {
        cumulative += w.total_distance ?? 0;
        return { date: w.date, cumulative };
    });

    const totalDistance = cumulative;
    const lastMillionBoundary = Math.floor(totalDistance / 1_000_000) * 1_000_000;
    const nextMillionTarget = lastMillionBoundary + 1_000_000;

    if (lastMillionBoundary === 0) {
        // Haven't hit first million yet - project from start
        const firstDate = new Date(sorted[0].date);
        const lastDate = new Date(sorted[sorted.length - 1].date);
        const daysSinceStart = (lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24);

        if (daysSinceStart <= 0 || totalDistance <= 0) return [];

        const metersPerDay = totalDistance / daysSinceStart;
        const daysToNextMillion = (nextMillionTarget - totalDistance) / metersPerDay;
        const projectedDate = new Date(lastDate.getTime() + daysToNextMillion * 24 * 60 * 60 * 1000);

        return [
            { date: sorted[0].date, cumulativeDistance: 0 },
            { date: projectedDate.toISOString().slice(0, 19).replace('T', ' '), cumulativeDistance: nextMillionTarget }
        ];
    }

    // Find when we crossed the last million boundary
    let crossingIndex = cumulativeData.findIndex(d => d.cumulative >= lastMillionBoundary);
    if (crossingIndex < 0) return [];

    const crossingPoint = cumulativeData[crossingIndex];
    const lastPoint = cumulativeData[cumulativeData.length - 1];

    const crossingDate = new Date(crossingPoint.date);
    const lastDate = new Date(lastPoint.date);
    const daysSinceCrossing = (lastDate.getTime() - crossingDate.getTime()) / (1000 * 60 * 60 * 24);

    if (daysSinceCrossing <= 0) return [];

    const metersSinceCrossing = lastPoint.cumulative - lastMillionBoundary;
    const metersPerDay = metersSinceCrossing / daysSinceCrossing;

    if (metersPerDay <= 0) return [];

    const metersToNextMillion = nextMillionTarget - lastPoint.cumulative;
    const daysToNextMillion = metersToNextMillion / metersPerDay;
    const projectedDate = new Date(lastDate.getTime() + daysToNextMillion * 24 * 60 * 60 * 1000);

    return [
        { date: crossingPoint.date, cumulativeDistance: lastMillionBoundary },
        { date: projectedDate.toISOString().slice(0, 19).replace('T', ' '), cumulativeDistance: nextMillionTarget }
    ];
}

export default function CumulativeDistance() {
    const loaderData: any = useLoaderData();

    return (
        <div>
            <Suspense fallback={
                <div>
                    <h1>Loading workout data...</h1>
                    <ClimbingBoxLoader color="#574f6e" />
                </div>
            }>
                <Await resolve={Promise.all([loaderData.data, loaderData.metadata])}>
                    {(resolvedData) => {
                        const workouts: Workout[] = resolvedData[0];
                        const metadata: any = resolvedData[1];
                        const projectionData = computeProjectionData(workouts);

                        const spec: any = {
                            config: VEGA_CONFIG,
                            width: VEGA_WIDTH,
                            height: VEGA_HEIGHT,
                            title: {
                                text: "Cumulative Meters Rowed",
                                subtitle: `${metadata.totalDistance.toLocaleString()}m total to date`,
                            },
                            params: [{
                                name: "showProjection",
                                value: false,
                                bind: { input: "checkbox", name: "Show projection to next million  " }
                            }],
                            layer: [
                                {
                                    mark: 'area',
                                    transform: [{
                                        sort: [{ "field": "date" }],
                                        "window": [{ "op": "sum", "field": "total_distance", "as": "Total Distance (m)" }],
                                        "frame": [null, 0]
                                    }],
                                    encoding: {
                                        x: { field: 'date', type: 'temporal', axis: { title: "Date of Workout" } },
                                        y: { field: 'Total Distance (m)', type: 'quantitative' },
                                        tooltip: [
                                            { field: "date", type: "temporal" },
                                            { field: "total_distance", type: "quantitative", title: "Workout Distance" },
                                            { field: "Total Distance (m)", type: "quantitative" }
                                        ]
                                    },
                                    data: { name: 'table' },
                                },
                                {
                                    mark: {
                                        type: 'line',
                                        strokeDash: [6, 4],
                                        strokeWidth: 3,
                                        point: { filled: true, size: 100 }
                                    },
                                    transform: [
                                        { filter: "showProjection" }
                                    ],
                                    encoding: {
                                        x: { field: 'date', type: 'temporal' },
                                        y: { field: 'cumulativeDistance', type: 'quantitative' },
                                        color: { value: VEGA_REGRESSION_COLOR },
                                        tooltip: [
                                            { field: "date", type: "temporal" },
                                            { field: "cumulativeDistance", type: "quantitative", title: "Projected Distance", format: ",.0f" }
                                        ]
                                    },
                                    data: { name: 'projection' },
                                }
                            ],
                        };

                        return <VegaEmbed spec={spec} onEmbed={(result) => {
                            result.view.data('table', workouts);
                            result.view.data('projection', projectionData).run();
                        }} />;
                    }}
                </Await>
            </Suspense>
            <div><b>Distance includes rest distance.</b></div>
        </div>
    );
}
